import { useEffect, useState } from 'react';

interface ShareDialogProps {
  open: boolean;
  onClose: () => void;
  title: string;
}

const channels = [
  { icon: 'ri-weibo-fill', label: '微博', color: 'text-accent-500' },
  { icon: 'ri-wechat-fill', label: '微信', color: 'text-primary-600' },
  { icon: 'ri-qq-fill', label: 'QQ', color: 'text-secondary-500' },
  { icon: 'ri-link', label: '复制链接', color: 'text-foreground-600' },
];

export default function ShareDialog({ open, onClose, title }: ShareDialogProps) {
  const [copied, setCopied] = useState(false);
  const [url, setUrl] = useState('');

  useEffect(() => {
    if (open) {
      setUrl(window.location.href);
      setCopied(false);
    }
  }, [open]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    if (open) document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  const handleChannel = async (label: string) => {
    if (label === '复制链接') {
      try {
        await navigator.clipboard.writeText(url);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch {
        setCopied(false);
      }
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center px-4">
      <button
        type="button"
        aria-label="关闭分享"
        onClick={onClose}
        className="absolute inset-0 cursor-pointer bg-foreground-950/50"
      />
      <div className="relative w-full max-w-sm animate-slideDown rounded-lg bg-background-50 p-6">
        <div className="flex items-start justify-between">
          <div>
            <h3 className="font-heading text-lg font-bold text-foreground-950">分享这条内容</h3>
            <p className="mt-1 line-clamp-1 text-xs text-foreground-500">{title}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="flex h-8 w-8 items-center justify-center rounded-md text-foreground-500 hover:bg-background-100"
            aria-label="关闭"
          >
            <i className="ri-close-line text-xl" />
          </button>
        </div>

        <div className="mt-5 flex justify-center rounded-lg border border-background-200 bg-background-100 p-4">
          {url ? (
            <img
              src={`https://api.qrserver.com/v1/create-qr-code/?size=160x160&data=${encodeURIComponent(url)}`}
              alt="分享二维码"
              width={160}
              height={160}
              className="h-40 w-40 rounded-md bg-white p-1"
            />
          ) : null}
        </div>
        <p className="mt-3 text-center text-xs text-foreground-500">扫码即可在手机端打开此页面</p>

        <div className="mt-5 grid grid-cols-4 gap-3">
          {channels.map((c) => (
            <button
              key={c.label}
              type="button"
              onClick={() => handleChannel(c.label)}
              className="flex flex-col items-center gap-2 rounded-md py-2 transition-colors hover:bg-background-100"
            >
              <span className={`flex h-11 w-11 items-center justify-center rounded-full bg-background-100 ${c.color}`}>
                <i className={`${c.icon} text-xl`} />
              </span>
              <span className="text-xs text-foreground-600">{c.label}</span>
            </button>
          ))}
        </div>

        {copied && (
          <p className="mt-3 flex items-center justify-center gap-1 text-xs text-primary-600">
            <i className="ri-check-line" />
            链接已复制到剪贴板
          </p>
        )}
        <p className="mt-3 text-center text-[11px] text-foreground-400">分享功能为前端演示，不连接第三方接口</p>
      </div>
    </div>
  );
}